<template>
  <div class="committee-history">
    <AppSectionHero
      title="Review History"
      subtitle="Browse completed committee review decisions, inspect prior comments, and keep past cases easy to reference."
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectDecisionStatus"
    />

    <CCard class="border-0 shadow-sm table-shell">
      <CCardBody class="p-4">
        <div class="table-head">
          <div class="table-head__copy">Showing {{ paginatedHistory.length }} of {{ filteredHistory.length }} reviews</div>
          <CButton color="secondary" variant="outline" size="sm" @click="toggleSort">Sort by Date {{ sortDirection === 'desc' ? '↓' : '↑' }}</CButton>
        </div>

        <div v-if="paginatedHistory.length" class="history-table">
          <div class="history-table__header">
            <div>Agreement</div>
            <div>Type</div>
            <div>Partner</div>
            <div>Status</div>
            <div>Reviewed</div>
            <div>Actions</div>
          </div>

          <div
            v-for="item in paginatedHistory"
            :key="item.id"
            class="history-table__row"
          >
            <div class="history-table__cell history-table__cell--agreement">
              <div class="history-title">{{ item.agreement }}</div>
              <div class="history-meta">{{ item.id }}</div>
            </div>
            <div class="history-table__cell">
              <span class="history-value">{{ item.type }}</span>
            </div>
            <div class="history-table__cell">
              <span class="history-value">{{ item.partner }}</span>
            </div>
            <div class="history-table__cell">
              <span class="decision-pill" :class="`decision-pill--${item.decision}`">{{ formatDecision(item.decision) }}</span>
            </div>
            <div class="history-table__cell history-table__cell--date">
              <span class="history-date">{{ formatDate(item.reviewDate) }}</span>
            </div>
            <div class="history-table__cell history-table__cell--actions">
              <CButton color="secondary" variant="outline" size="sm" class="history-action-btn" @click="openDetails(item)">View</CButton>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <div class="empty-state__title">No reviews found</div>
          <div class="empty-state__copy">Try changing the filters or wait for more committee decisions to be recorded.</div>
        </div>

        <div class="pagination" v-if="filteredHistory.length">
          <div class="pagination-actions">
            <button
              v-if="currentPage > 1"
              type="button"
              class="page-btn history-page-btn"
              @click="currentPage -= 1"
            >
              ← Previous
            </button>
            <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
            <button
              type="button"
              class="page-btn primary-btn history-page-btn history-page-btn--next"
              :disabled="currentPage === totalPages"
              @click="currentPage += 1"
            >
              Next →
            </button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm chart-shell">
      <CCardBody class="p-4">
        <div class="section-title">Review Performance</div>
        <div class="chart-grid">
          <div v-for="item in monthlyBars" :key="item.label" class="chart-col">
            <div class="chart-stack">
              <div class="chart-segment chart-segment--approved" :style="{ height: item.approvedHeight + 'px' }"></div>
              <div class="chart-segment chart-segment--changes" :style="{ height: item.changesHeight + 'px' }"></div>
              <div class="chart-segment chart-segment--rejected" :style="{ height: item.rejectedHeight + 'px' }"></div>
            </div>
            <div class="chart-label">{{ item.label }}</div>
            <div class="chart-total">{{ item.total }}</div>
          </div>
        </div>
        <div class="legend">
          <span><i class="legend-dot legend-dot--approved" /> Approved</span>
          <span><i class="legend-dot legend-dot--changes" /> Review</span>
          <span><i class="legend-dot legend-dot--rejected" /> Rejected</span>
        </div>
      </CCardBody>
    </CCard>

    <CModal :show.sync="showDetailsModal" size="lg" :centered="true">
      <CModalHeader closeButton>
        <CModalTitle>{{ selectedReview ? selectedReview.agreement : 'Review Details' }}</CModalTitle>
      </CModalHeader>
      <CModalBody v-if="selectedReview">
        <div class="detail-grid">
          <div class="detail-item"><span>Review ID</span><strong>{{ selectedReview.id }}</strong></div>
          <div class="detail-item"><span>Decision</span><strong>{{ formatDecision(selectedReview.decision) }}</strong></div>
          <div class="detail-item"><span>Review Date</span><strong>{{ formatFullDateTime(selectedReview.reviewDate) }}</strong></div>
          <div class="detail-item"><span>Review Time</span><strong>{{ calculateReviewTime(selectedReview) }}</strong></div>
        </div>

        <div class="detail-block" v-if="selectedReview.approvalNotes">
          <div class="detail-block__title">Approval Notes</div>
          <p>{{ selectedReview.approvalNotes }}</p>
        </div>

        <div class="detail-block" v-if="selectedReview.requestedChanges && selectedReview.requestedChanges.length">
          <div class="detail-block__title">Requested Changes</div>
          <ul>
            <li v-for="(item, index) in selectedReview.requestedChanges" :key="index">{{ item }}</li>
          </ul>
        </div>

        <div class="detail-block" v-if="selectedReview.rejectionReason">
          <div class="detail-block__title">Rejection Reason</div>
          <p>{{ selectedReview.rejectionReason }}</p>
        </div>

        <div class="detail-block">
          <div class="detail-block__title">Review Comments</div>
          <div v-if="selectedReview.comments && selectedReview.comments.length">
            <div v-for="comment in selectedReview.comments" :key="comment.id" class="comment-card">
              <div class="comment-head">
                <strong>{{ comment.author }}</strong>
                <span>{{ formatFullDateTime(comment.date) }}</span>
              </div>
              <p>{{ comment.content }}</p>
              <small v-if="comment.suggestedChange">{{ comment.suggestedChange }}</small>
            </div>
          </div>
          <div v-else class="muted-copy">No committee comments saved for this review.</div>
        </div>

        <div class="detail-block">
          <div class="detail-block__title">Attachments</div>
          <div v-if="selectedReview.attachments && selectedReview.attachments.length">
            <div v-for="file in selectedReview.attachments" :key="file.id" class="attachment-row">
              <span>{{ file.name }}</span>
              <span>{{ file.size }}</span>
            </div>
          </div>
          <div v-else class="muted-copy">No attachments available.</div>
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" @click="showDetailsModal = false">Close</CButton>
      </CModalFooter>
    </CModal>
  </div>
</template>

<script>
import Service from '@/service/api'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { readPageCache, writePageCache } from '@/utils/page-cache'

const COMMITTEE_HISTORY_CACHE_KEY = 'committee-review-history-page-v2'
const COMMITTEE_HISTORY_CACHE_MAX_AGE = 5 * 60 * 1000

function isFreshTimestamp(value, maxAgeMs) {
  if (!value) return false
  const timestamp = new Date(value).getTime()
  if (!timestamp) return false
  return (Date.now() - timestamp) < maxAgeMs
}

export default {
  name: 'CommitteeReviewHistoryPage',
  components: { AppSectionHero },
  data() {
    return {
      history: [],
      lastUpdatedAt: '',
      currentPage: 1,
      pageSize: 8,
      sortDirection: 'desc',
      showDetailsModal: false,
      selectedReview: null,
      filters: {
        decision: 'all',
        type: 'all'
      }
    }
  },
  computed: {
    filteredHistory() {
      let items = [...this.history]
      if (this.filters.decision !== 'all') {
        items = items.filter(item => item.decision === this.filters.decision)
      }
      items.sort((a, b) => {
        const dateA = new Date(a.reviewDate).getTime()
        const dateB = new Date(b.reviewDate).getTime()
        return this.sortDirection === 'desc' ? dateB - dateA : dateA - dateB
      })
      return items
    },
    paginatedHistory() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredHistory.slice(start, start + this.pageSize)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredHistory.length / this.pageSize))
    },
    paginationStart() {
      return this.filteredHistory.length ? ((this.currentPage - 1) * this.pageSize) + 1 : 0
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredHistory.length)
    },
    typeOptions() {
      return [...new Set(this.history.map(item => item.type).filter(Boolean))]
    },
    heroStats() {
      const approved = this.filteredHistory.filter(item => item.decision === 'approved').length
      const changes = this.filteredHistory.filter(item => item.decision === 'changes-requested').length
      const rejected = this.filteredHistory.filter(item => item.decision === 'rejected').length
      return [
        { label: 'Approved', value: approved, icon: 'cil-check-circle', iconClass: 'app-section-stat__icon--active', clickable: true, active: this.filters.decision === 'approved' },
        { label: 'Review', value: changes, icon: 'cil-loop-circular', iconClass: 'app-section-stat__icon--attention', clickable: true, active: this.filters.decision === 'changes-requested' },
        { label: 'Rejected', value: rejected, icon: 'cil-x-circle', iconClass: 'app-section-stat__icon--danger', clickable: true, active: this.filters.decision === 'rejected' }
      ]
    },
    monthlyBars() {
      const months = Array.from({ length: 6 }).map((_, index) => {
        const date = new Date()
        date.setMonth(date.getMonth() - (5 - index))
        return {
          key: `${date.getFullYear()}-${date.getMonth() + 1}`,
          label: date.toLocaleString('en-US', { month: 'short' })
        }
      })
      return months.map(month => {
        const items = this.history.filter(item => {
          const date = new Date(item.reviewDate)
          return `${date.getFullYear()}-${date.getMonth() + 1}` === month.key
        })
        const approved = items.filter(item => item.decision === 'approved').length
        const changes = items.filter(item => item.decision === 'changes-requested').length
        const rejected = items.filter(item => item.decision === 'rejected').length
        const scale = 18
        return {
          label: month.label,
          total: items.length,
          approvedHeight: approved * scale,
          changesHeight: changes * scale,
          rejectedHeight: rejected * scale
        }
      })
    }
  },
  watch: {
    filteredHistory() {
      if (this.currentPage > this.totalPages) this.currentPage = this.totalPages
    }
  },
  mounted() {
    this.loadCachedHistory()
    if (!isFreshTimestamp(this.lastUpdatedAt, COMMITTEE_HISTORY_CACHE_MAX_AGE)) {
      this.loadHistory()
    }
  },
  methods: {
    applyHistory(items) {
      this.history = Array.isArray(items) ? items : []
    },
    loadCachedHistory() {
      const cached = readPageCache(COMMITTEE_HISTORY_CACHE_KEY, COMMITTEE_HISTORY_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.applyHistory(cached.history)
      this.lastUpdatedAt = cached.lastUpdatedAt || ''
    },
    async loadHistory() {
      const response = await Service.committeeReview('history')
      this.applyHistory(response && response.data && response.data.data ? response.data.data : [])
      this.lastUpdatedAt = new Date().toISOString()
      writePageCache(COMMITTEE_HISTORY_CACHE_KEY, {
        history: this.history,
        lastUpdatedAt: this.lastUpdatedAt
      })
    },
    toggleSort() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc'
    },
    openDetails(item) {
      this.selectedReview = item
      this.showDetailsModal = true
    },
    selectDecisionStatus(stat) {
      const label = String(stat && stat.label ? stat.label : '').toLowerCase()
      const nextDecision = label === 'approved'
        ? 'approved'
        : label === 'in review'
          ? 'changes-requested'
          : label === 'rejected'
            ? 'rejected'
            : 'all'
      this.filters.decision = this.filters.decision === nextDecision ? 'all' : nextDecision
    },
    formatDecision(decision) {
      return {
        approved: 'Approved',
        'changes-requested': 'Review',
        rejected: 'Rejected',
        'sent-to-admin': 'Sent to Admin'
      }[decision] || decision
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatFullDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    calculateReviewTime(review) {
      if (!(review && review.assignedDate && review.completedDate)) return 'N/A'
      const diffMs = new Date(review.completedDate) - new Date(review.assignedDate)
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      return diffDays > 0 ? `${diffDays}d ${diffHours}h` : `${diffHours}h`
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("committee-history");
@include workspace-button-system;

.filter-shell, .table-shell, .chart-shell { border-radius: 18px; margin-bottom: 16px; }
.table-head, .pagination { align-items: center; display: flex; justify-content: space-between; gap: 12px; }
.table-head { margin-bottom: 14px; }
.table-head__copy, .history-meta, .history-date, .history-time, .muted-copy { color: #718096; }
.history-table {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  overflow: hidden;
}
.history-table__header,
.history-table__row {
  align-items: center;
  column-gap: 20px;
  display: grid;
  grid-template-columns: minmax(220px, 1.5fr) minmax(120px, 0.8fr) minmax(170px, 0.95fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) 108px;
  padding: 18px 20px;
}
.history-table__header {
  background: #f8fafc;
  border-bottom: 1px solid #e6ebf1;
  color: #52606d;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.history-table__row {
  border-bottom: 1px solid #eef1f6;
}
.history-table__row:last-child {
  border-bottom: 0;
}
.history-table__cell {
  min-width: 0;
}
.history-table__cell--date {
  display: grid;
  gap: 4px;
}
.history-table__cell--actions {
  display: flex;
  justify-content: center;
}
.history-title, .section-title, .detail-block__title { color: #344767; font-weight: 700; }
.history-value {
  color: #344767;
  display: inline-block;
  line-height: 1.5;
}
.decision-pill {
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  justify-content: center;
  line-height: 1.35;
  min-width: 112px;
  padding: 8px 12px;
  text-align: center;
  white-space: normal;
}
.decision-pill--approved { background: #ecfdf3; color: #027a48; }
.decision-pill--changes-requested { background: #fff4df; color: #b54708; }
.decision-pill--sent-to-admin { background: #eff8ff; color: #175cd3; }
.decision-pill--rejected { background: #fff1f2; color: #b42318; }
.history-action-btn {
  font-weight: 800;
  min-height: 40px;
  min-width: 72px;
}
.history-action-btn:hover,
.history-action-btn:focus {
  color: #223045;
}
.empty-state { padding: 32px 8px; text-align: center; }
.empty-state__title { color: #344767; font-weight: 700; margin-bottom: 6px; }
.empty-state__copy { color: #718096; }
.pagination { border-top: 1px solid #eef1f6; justify-content: center; margin-top: 16px; padding: 16px 0 12px; }
.pagination-actions { align-items: center; display: flex; flex: 1; gap: 16px; justify-content: center; position: relative; }
.pagination-actions .history-page-btn:first-child { left: 0; position: absolute; }
.pagination-actions .history-page-btn:last-child { position: absolute; right: 0; }
.history-page-btn--next {
  background: #9e212f !important;
  border-color: transparent !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  color: #fff !important;
}
.history-page-btn--next:hover:not(:disabled) {
  background: #9e212f !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  transform: none !important;
}
.history-page-btn--next:active:not(:disabled) {
  background: #9e212f !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  transform: none !important;
}
.history-page-btn--next:disabled {
  background: #f7d9dd !important;
  border-color: #f7d9dd !important;
  color: #fff !important;
}
.page-indicator { color: #52606d; font-size: 0.9rem; font-weight: 700; }
.chart-grid { align-items: end; display: grid; gap: 18px; grid-template-columns: repeat(6, minmax(0, 1fr)); margin-top: 18px; }
.chart-col { display: grid; justify-items: center; }
.chart-stack {
  align-items: end;
  background: #f8fafc;
  border-radius: 12px 12px 6px 6px;
  display: flex;
  flex-direction: column-reverse;
  height: 180px;
  justify-content: flex-start;
  overflow: hidden;
  width: 40px;
}
.chart-segment { width: 100%; }
.chart-segment--approved { background: #16a34a; }
.chart-segment--changes { background: #f59e0b; }
.chart-segment--rejected { background: #dc2626; }
.chart-label, .chart-total { color: #52606d; font-size: 0.82rem; margin-top: 8px; }
.legend { display: flex; gap: 18px; margin-top: 16px; }
.legend span { align-items: center; color: #52606d; display: inline-flex; gap: 8px; }
.legend-dot { border-radius: 999px; display: inline-block; height: 10px; width: 10px; }
.legend-dot--approved { background: #16a34a; }
.legend-dot--changes { background: #f59e0b; }
.legend-dot--rejected { background: #dc2626; }
.detail-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 16px; }
.detail-item { background: #f8fafc; border-radius: 12px; display: grid; gap: 4px; padding: 14px; }
.detail-item span { color: #718096; font-size: 0.82rem; font-weight: 700; text-transform: uppercase; }
.detail-item strong { color: #344767; }
.detail-block { margin-top: 18px; }
.comment-card, .attachment-row {
  background: #f8fafc;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  margin-top: 10px;
  padding: 12px 14px;
}
.comment-head { align-items: center; color: #52606d; display: flex; justify-content: space-between; margin-bottom: 8px; }
.attachment-row { align-items: center; display: flex; justify-content: space-between; }
@media (max-width: 900px) {
  .table-head, .pagination { align-items: stretch; flex-direction: column; }
  .history-table__header,
  .history-table__row {
    grid-template-columns: minmax(180px, 1.45fr) minmax(110px, 0.8fr) minmax(140px, 0.95fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) 96px;
  }
  .chart-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .pagination-actions .history-page-btn:first-child,
  .pagination-actions .history-page-btn:last-child { position: static; }
  .history-table__header {
    display: none;
  }
  .history-table__row {
    align-items: flex-start;
    grid-template-columns: 1fr;
    row-gap: 10px;
  }
  .history-table__cell--actions {
    justify-content: flex-start;
  }
  .detail-grid { grid-template-columns: 1fr; }
  .chart-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
